import { router } from "@inertiajs/react";
import { useState } from "react";

import ConfirmDialog from "@/components/ConfirmDialog";
import type { Column, PagyMeta, RowAction } from "@/components/DataTable";
import DataTable from "@/components/DataTable";
import FilterBar, { type FilterField } from "@/components/FilterBar";
import { useGenderedText } from "@/lib/genderedText";
import { usePermissions } from "@/lib/permissions";
import { users } from "@/routes";
import type { User } from "@/types";

const USER_CATEGORY_LABELS: Record<string, string> = {
  senora: "Señora",
  universitaria: "Universitaria",
  numeraria_auxiliar: "Numeraria Auxiliar",
  agregada: "Agregada",
  numeraria: "Numeraria",
  la_cuesta: "S V",
  la_cuesta_sn: "S SV",
  la_cuesta_cp: "CP SV",
};

const ROLE_FILTER_OPTIONS = [
  { value: "is_admin", key: "isAdmin" },
  { value: "is_house_manager", key: "isHouseManager" },
  { value: "is_house_master", key: "isHouseMaster" },
  { value: "is_director", key: "isDirector" },
  { value: "is_student", key: "isStudent" },
  { value: "is_subscriber", key: "isSubscriber" },
  { value: "is_center_director", key: "isCenterDirector" },
] as const;

type Props = {
  users: User[];
  pagy: PagyMeta;
  filters: Record<string, string>;
  centers: { id: number; name: string }[];
};

const COLUMNS: Column<User>[] = [
  {
    header: "Nombre",
    className: "max-w-72",
    cell: (u) => {
      const name = (u.full_name as string) || "-";
      return (
        <span className="block truncate" title={name}>
          {name}
        </span>
      );
    },
  },
  {
    header: "RUT",
    cell: (u) => (u.rut_formatted as string) || u.rut,
  },
  {
    header: "Categoría",
    cell: (u) =>
      u.user_category
        ? (USER_CATEGORY_LABELS[u.user_category] ?? u.user_category)
        : "-",
  },
  {
    header: "Email",
    cell: (u) => u.email || "-",
  },
  {
    header: "Teléfono",
    cell: (u) => u.phone_number || "-",
  },
  {
    header: "Centro",
    cell: (u) => (u.center_name as string | null) || "-",
  },
  {
    header: "Roles",
    cell: (u) => (u.roles as string) || "-",
  },
];

export default function UsersIndex({
  users: rows,
  pagy,
  filters,
  centers,
}: Props) {
  const permissions = usePermissions();
  const t = useGenderedText();
  const label = t("usuarios");
  const emptyMsg = t("usuariosEmptyState");

  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);

  const actions: RowAction<User>[] = [
    {
      label: "Ver",
      href: (u) => users.profile(u.id).url,
      variant: "primary",
    },
    {
      label: "Editar",
      href: (u) => users.edit(u.id).url,
      variant: "primary",
    },
    ...(permissions.canManageAdminOnlyRecords
      ? ([
          {
            label: "Eliminar",
            onClick: (u) => setDeleteTarget(u),
            variant: "danger",
          },
        ] satisfies RowAction<User>[])
      : []),
  ];

  const filterFields: FilterField[] = [
    {
      key: "nombre",
      label: "Nombre",
      type: "text",
      placeholder: "Buscar por nombre...",
    },
    {
      key: "rut",
      label: "RUT",
      type: "text",
      placeholder: "Buscar por RUT...",
    },
    {
      key: "user_category",
      label: "Categoría",
      type: "select",
      options: [
        { label: "Todas", value: "" },
        ...Object.entries(USER_CATEGORY_LABELS).map(([value, label]) => ({
          value,
          label,
        })),
      ],
    },
    {
      key: "role",
      label: "Rol",
      type: "select",
      options: [
        { label: "Todos", value: "" },
        ...ROLE_FILTER_OPTIONS.map(({ value, key }) => ({
          label: t(key),
          value,
        })),
      ],
    },
    {
      key: "center_id",
      label: "Centro",
      type: "select",
      options: [
        { label: "Todos", value: "" },
        ...centers.map((c) => ({ label: c.name, value: String(c.id) })),
      ],
    },
  ];

  function navigate(
    params: Record<string, string | number>,
    opts?: { replace?: boolean },
  ) {
    router.get(users.index().url, params, {
      preserveScroll: true,
      replace: opts?.replace ?? false,
    });
  }

  return (
    <>
      <div className="w-full py-10">
        <div className="px-6 sm:px-8 lg:px-12">
          <h1 className="mb-6 font-sans text-3xl font-semibold text-ink">
            {label}
          </h1>

          <FilterBar
            fields={filterFields}
            values={filters}
            onChange={(newFilters) =>
              navigate({ ...newFilters, page: 1 }, { replace: true })
            }
          />
        </div>

        <DataTable
          columns={COLUMNS}
          rows={rows}
          keyExtractor={(u) => u.id}
          actions={actions}
          utilities={
            <a
              href={
                users.exportExcel({
                  query: Object.fromEntries(
                    Object.entries(filters).filter(([, v]) => v !== ""),
                  ),
                }).url
              }
              className="font-sans text-sm font-light text-ink underline underline-offset-2 hover:text-steel"
            >
              Exportar Excel
            </a>
          }
          pagy={pagy}
          onPageChange={(page) => navigate({ ...filters, page })}
          emptyMessage={emptyMsg}
        />
      </div>

      <ConfirmDialog
        open={deleteTarget !== null}
        title={t("eliminarUsuario")}
        message={`${t("eliminarUsuarioMensaje")} ${(deleteTarget?.full_name as string) ?? ""} y sus inscripciones.`}
        confirmLabel="Eliminar"
        variant="danger"
        onConfirm={() => {
          if (deleteTarget) {
            router.delete(users.destroy(deleteTarget.id).url, {
              preserveScroll: true,
            });
          }
          setDeleteTarget(null);
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}
